import { useEffect, useId, useMemo, useState } from "react";
import { FiPlus, FiSave } from "react-icons/fi";
import { Input, Modal } from "@iq/models";
import {
	createTask,
	getProjectById,
	getTeamMembers,
	TASK_PRIORITIES,
	TASK_STATUSES,
} from "../../Services/pm_service";
import "../../../CRM/components/modals/Modal.css";

function toStatusLabel(status) {
	return String(status || "")
		.split("_")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function toPriorityLabel(priority) {
	const normalized = String(priority || "").toLowerCase();
	if (normalized === "urgent") {
		return "Urgent";
	}
	if (normalized === "high") {
		return "High";
	}
	if (normalized === "medium") {
		return "Medium";
	}
	if (normalized === "low") {
		return "Low";
	}
	return "Medium";
}

function CreateTaskModal({ isOpen, onClose, projectId, initialStatus = "not_started", onCreated }) {
	const formId = useId();
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		assignedUserId: null,
		status: initialStatus,
		priority: "medium",
		dueDate: "",
	});
	const [selectedAssigneeLabel, setSelectedAssigneeLabel] = useState("");
	const [selectedStatusLabel, setSelectedStatusLabel] = useState(toStatusLabel(initialStatus));
	const [selectedPriorityLabel, setSelectedPriorityLabel] = useState("Medium");
	const [teamMembers, setTeamMembers] = useState([]);
	const [membersLoading, setMembersLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const canSubmit = useMemo(() => {
		return Boolean(formData.title.trim()) && Boolean(projectId) && !loading;
	}, [formData.title, projectId, loading]);

	const actionButtons = useMemo(
		() => [
			{
				key: "create-task",
				text: loading ? "Creating..." : "Create Task",
				icon: <FiSave />,
				type: "submit",
				form: formId,
				disabled: !canSubmit,
				color: "accent",
			},
		],
		[canSubmit, formId, loading]
	);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		let cancelled = false;

		async function loadContext() {
			setMembersLoading(true);
			try {
				const [members, project] = await Promise.all([
					getTeamMembers(),
					projectId ? getProjectById(projectId) : Promise.resolve(null),
				]);

				if (cancelled) {
					return;
				}

				const projectMembers = Array.isArray(project?.members) ? project.members : [];
				const allowedUserIds = new Set(projectMembers.map((member) => Number(member?.user_id || member?.user?.id)));

				const filteredMembers = (Array.isArray(members) ? members : []).filter((member) =>
					allowedUserIds.has(Number(member?.id))
				);
				setTeamMembers(filteredMembers);
			} catch (contextError) {
				if (!cancelled) {
					setTeamMembers([]);
					setError(contextError?.message || "Failed to load task form data");
				}
			} finally {
				if (!cancelled) {
					setMembersLoading(false);
				}
			}
		}

		setFormData({
			title: "",
			description: "",
			assignedUserId: null,
			status: initialStatus,
			priority: "medium",
			dueDate: "",
		});
		setSelectedAssigneeLabel("");
		setSelectedStatusLabel(toStatusLabel(initialStatus));
		setSelectedPriorityLabel("Medium");
		setError("");

		loadContext();

		return () => {
			cancelled = true;
		};
	}, [isOpen, initialStatus, projectId]);

	if (!isOpen) {
		return null;
	}

	function handleInputChange(event) {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError("");
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setError("");

		if (!projectId) {
			setError("Please select a project first");
			return;
		}

		const title = formData.title.trim();
		if (!title) {
			setError("Task title is required");
			return;
		}

		try {
			setLoading(true);
			const createdTask = await createTask(projectId, {
				title,
				description: formData.description.trim(),
				assignedUserId: formData.assignedUserId || undefined,
				status: formData.status,
				priority: formData.priority,
				dueDate: formData.dueDate ? formData.dueDate : undefined,
			});

			onCreated?.(createdTask);
			onClose?.();
		} catch (submitError) {
			setError(submitError?.message || "Failed to create task");
		} finally {
			setLoading(false);
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Create Task"
			icon={<FiPlus />}
			actionButtons={actionButtons}
			errorMessage={error}
			shrink
		>
			<form id={formId} onSubmit={handleSubmit} className="modal-form-container">
				<div className="form-fields-container">
					<div className="modal-form-field">
						<label htmlFor="pm-task-title">
							Task Title <span style={{ color: "var(--color-danger)" }}>*</span>
						</label>
						<Input
							id="pm-task-title"
							type="text"
							name="title"
							value={formData.title}
							onChange={handleInputChange}
							placeholder="E.g. Draft onboarding email"
							required
							disabled={loading}
							colorScheme="light"
						/>
					</div>
					<div className="modal-form-field">
						<label>Assignee</label>
						{membersLoading ? (
							<div className="attendee-loading">Loading project members...</div>
						) : (
							<Input
								type="dropdown"
								id="pm-task-assignee"
								name="pm-task-assignee"
								placeholder="Unassigned"
								value={selectedAssigneeLabel}
								allowCustom={false}
								colorScheme="light"
								ariaLabel="Task assignee"
								renderInPortal={false}
								disabled={loading}
							>
								{({ closeMenu }) => (
									<>
										<button
											type="button"
											className="dropdown-menu-item"
											onClick={() => {
												setFormData((prev) => ({ ...prev, assignedUserId: null }));
												setSelectedAssigneeLabel("");
												closeMenu();
											}}
										>
											Unassigned
										</button>
										{teamMembers.map((member) => (
											<button
												key={member.id}
												type="button"
												className="dropdown-menu-item"
												onClick={() => {
													setFormData((prev) => ({ ...prev, assignedUserId: member.id }));
													setSelectedAssigneeLabel(member.name || member.email || "");
													closeMenu();
												}}
											>
												{member.name} ({member.email})
											</button>
										))}
										{teamMembers.length === 0 ? (
											<div className="dropdown-menu-empty">No project members available</div>
										) : null}
									</>
								)}
							</Input>
						)}
					</div>
					<div className="modal-form-field wide-form-field">
						<label htmlFor="pm-task-description">Description</label>
						<Input
							id="pm-task-description"
							type="textarea"
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							placeholder="Optional task details"
							rows={3}
							disabled={loading}
							colorScheme="light"
						/>
					</div>



					<div className="modal-form-field modal-form-field-skinny">
						<label>Status</label>
						<Input
							type="dropdown"
							id="pm-task-status"
							name="pm-task-status"
							placeholder="Status"
							value={selectedStatusLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Task status"
							renderInPortal
							disabled={loading}
							options={TASK_STATUSES.map((status) => ({ value: status, label: toStatusLabel(status) }))}
							onOptionSelect={(option) => {
								setFormData((prev) => ({ ...prev, status: option.value }));
								setSelectedStatusLabel(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
						<label>Priority</label>
						<Input
							type="dropdown"
							id="pm-task-priority"
							name="pm-task-priority"
							placeholder="Priority"
							value={selectedPriorityLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Task priority"
							renderInPortal
							disabled={loading}
							options={TASK_PRIORITIES.map((priority) => ({ value: priority, label: toPriorityLabel(priority) }))}
							onOptionSelect={(option) => {
								setFormData((prev) => ({ ...prev, priority: option.value }));
								setSelectedPriorityLabel(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
						<label htmlFor="pm-task-due-date">Due Date</label>
						<Input
							id="pm-task-due-date"
							type="date"
							name="dueDate"
							value={formData.dueDate}
							onChange={handleInputChange}
							disabled={loading}
							colorScheme="light"
						/>
					</div>
				</div>
			</form>
		</Modal>
	);
}

export default CreateTaskModal;
